/**
 * "Add to Home Screen". Chrome/Android fires `beforeinstallprompt` we can defer
 * and trigger from a button; iOS Safari has no API, so we detect it and show the
 * manual Share-sheet hint instead.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const isStandalone =
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS
    (navigator as unknown as { standalone?: boolean }).standalone === true);

export const isIos =
  typeof navigator !== 'undefined' &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !/crios|fxios/i.test(navigator.userAgent);

class Install {
  #deferred: BeforeInstallPromptEvent | null = null;
  canPrompt = $state(false);
  installed = $state(isStandalone);

  init(): void {
    addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.#deferred = e as BeforeInstallPromptEvent;
      this.canPrompt = true;
    });
    addEventListener('appinstalled', () => {
      this.installed = true;
      this.canPrompt = false;
      this.#deferred = null;
    });
  }

  /** Show the browser's install prompt. Returns true if the user accepted. */
  async prompt(): Promise<boolean> {
    if (!this.#deferred) return false;
    await this.#deferred.prompt();
    const { outcome } = await this.#deferred.userChoice;
    this.#deferred = null;
    this.canPrompt = false;
    return outcome === 'accepted';
  }
}

export const install = new Install();
